import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { PlanType, SubscriptionStatus } from '@/types/database';
import { isUserProFromProfile } from '@/hooks/useIsUserPro';
import { 
  getCachedSubscription, 
  cacheProfile as cacheSubscriptionProfile,
} from '@/lib/offlineStorage';
import { cacheProfile as cacheOfflineDataProfile } from '@/lib/offlineDataCache';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import OneSignal from 'onesignal-cordova-plugin';
import {
  getCurrentPeriodValue,
  getFreeQuoteAllowance,
  getQuotePeriodStart,
} from '@/lib/subscriptionLimits';

const FREE_MONTHLY_LIMIT = 30;
const VISITOR_QUOTE_USED_KEY = 'orcapro_visitor_quote_used';
const GUEST_QUOTE_KEY = 'orcazap_guest_quote';

type OneSignalSubscriptionTag = 'free' | 'trial' | 'pro' | 'churned';

// Best-effort tag write; some OneSignal SDK versions differ in surface. Never throw.
function setOneSignalSubscriptionTag(state: OneSignalSubscriptionTag) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const anyOS = OneSignal as unknown as {
      User?: { addTag?: (k: string, v: string) => void };
    };
    if (anyOS.User?.addTag) {
      anyOS.User.addTag('subscription_state', state);
    }
  } catch (e) {
    console.warn('[SubscriptionContext] Failed to set OneSignal tag:', e);
  }
}

interface SubscriptionContextType {
  isPremium: boolean;
  planType: PlanType;
  subscriptionStatus: SubscriptionStatus;
  subscriptionEndDate: string | null;
  quotesCreatedInPeriod: number;
  quotaPeriodStart: string;
  canCreateQuote: boolean;
  remainingQuotes: number;
  freeQuotesLimit: number;
  incrementQuoteCount: (options?: {
    offline?: boolean;
    countTowardQuota?: boolean;
    sequenceId?: number | null;
  }) => Promise<void>;
  // Trial state
  isInTrial: boolean;
  trialDaysRemaining: number | null;
  trialEndsAt: string | null;
  // For visitors
  visitorQuoteUsed: boolean;
  setVisitorQuoteUsed: (used: boolean) => void;
  // Offline indicator
  isOfflineMode: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, profile, refreshProfile } = useAuth();
  const [visitorQuoteUsed, setVisitorQuoteUsedState] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [cachedSubscription, setCachedSubscription] = useState<ReturnType<typeof getCachedSubscription> | null>(null);
  const [quotaPeriodStart, setQuotaPeriodStart] = useState(() => getQuotePeriodStart());
  const [optimisticQuota, setOptimisticQuota] = useState<{
    userId: string;
    periodStart: string;
    quotesCreatedInPeriod: number;
    bonusQuotes: number;
  } | null>(null);
  const [hasSyncedRevenueCat, setHasSyncedRevenueCat] = useState(false);

  // Reset sync status when user logs out
  useEffect(() => {
    if (!user) {
      setHasSyncedRevenueCat(false);
      setOptimisticQuota(null);
    }
  }, [user]);

  useEffect(() => {
    let midnightTimer: number | null = null;

    const refreshLocalPeriod = () => {
      setQuotaPeriodStart(getQuotePeriodStart());
    };
    const scheduleNextLocalMidnight = () => {
      const now = new Date();
      const nextMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0,
        0,
        1,
      );
      midnightTimer = window.setTimeout(() => {
        refreshLocalPeriod();
        scheduleNextLocalMidnight();
      }, Math.max(1_000, nextMidnight.getTime() - now.getTime()));
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshLocalPeriod();
    };

    scheduleNextLocalMidnight();
    window.addEventListener('focus', refreshLocalPeriod);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      if (midnightTimer !== null) window.clearTimeout(midnightTimer);
      window.removeEventListener('focus', refreshLocalPeriod);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  // Sync RevenueCat subscription status with database on startup (native only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user || !profile || hasSyncedRevenueCat) return;

    const syncRevenueCatStatus = async () => {
      setHasSyncedRevenueCat(true); // prevent duplicate triggers
      try {
        console.log('[SubscriptionContext] Checking RevenueCat status to sync with database...');
        const { customerInfo } = await Purchases.getCustomerInfo();
        const entitlement = customerInfo.entitlements.active['orcamigo_pro_planos'];
        const hasActiveEntitlement = !!entitlement;
        const expirationDate = entitlement?.expirationDate ?? null;
        const willRenew = entitlement?.willRenew ?? true;
        // periodType exists on the RevenueCat CustomerInfo entitlement; 'TRIAL' or 'INTRO'
        // is what the SDK returns for free-trial or intro offers.
        const rcPeriodType = (entitlement as unknown as { periodType?: string })?.periodType;
        const isRcTrial = rcPeriodType === 'TRIAL' || rcPeriodType === 'INTRO';

        const dbIsPro = profile.plan_type === 'pro' || profile.subscription_status === 'active' || profile.subscription_status === 'trialing';
        const dbStatus = profile.subscription_status;

        // If RC says we're in a trial, mirror trial_started_at/trial_ends_at on the profile.
        // Only patch trial_started_at if it's still null (preserve the original start).
        if (hasActiveEntitlement && isRcTrial) {
          const trialUpdates: Record<string, unknown> = {};
          if (!profile.trial_started_at) trialUpdates.trial_started_at = new Date().toISOString();
          if (profile.trial_ends_at !== expirationDate) trialUpdates.trial_ends_at = expirationDate;
          if (!profile.trial_source) trialUpdates.trial_source = 'webhook';
          if (dbStatus !== 'trialing') trialUpdates.subscription_status = 'trialing';
          if (Object.keys(trialUpdates).length > 0) {
            const { error } = await supabase.from('profiles').update(trialUpdates).eq('id', user.id);
            if (error) {
              console.error('[SubscriptionContext] Error patching trial fields:', error);
            } else {
              await refreshProfile();
            }
          }
        }

        if (dbIsPro && !hasActiveEntitlement) {
          console.log('[SubscriptionContext] RevenueCat entitlement inactive but database is Pro. Downgrading database...');
          const { error } = await supabase
            .from('profiles')
            .update({
              plan_type: 'free',
              subscription_status: 'canceled',
              subscription_end_date: expirationDate,
              is_premium: false
            })
            .eq('id', user.id);

          if (!error) {
            console.log('[SubscriptionContext] Database downgraded successfully');
            await refreshProfile();
          } else {
            console.error('[SubscriptionContext] Error updating profile for downgrade:', error);
          }
        } else if (dbIsPro && hasActiveEntitlement && !willRenew && dbStatus !== 'canceled') {
          console.log('[SubscriptionContext] Entitlement still active but auto-renew is OFF. Marking as canceled...');
          const { error } = await supabase
            .from('profiles')
            .update({
              plan_type: 'free',
              subscription_status: 'canceled',
              subscription_end_date: expirationDate,
              is_premium: false,
            })
            .eq('id', user.id);

          if (!error) {
            console.log('[SubscriptionContext] Database marked as canceled (auto-renew off)');
            await refreshProfile();
          } else {
            console.error('[SubscriptionContext] Error marking profile as canceled:', error);
          }
        } else if (!dbIsPro && hasActiveEntitlement) {
          console.log('[SubscriptionContext] RevenueCat entitlement active but database is free. Upgrading database...');
          const { error } = await supabase
            .from('profiles')
            .update({
              plan_type: 'pro',
              subscription_status: 'active',
              subscription_end_date: expirationDate,
              is_premium: true,
              // Clear the free-plan company choice so the picker reappears
              // if the user downgrades again in the future.
              free_plan_active_company_id: null,
            })
            .eq('id', user.id);

          if (!error) {
            console.log('[SubscriptionContext] Database upgraded successfully');
            await refreshProfile();
          } else {
            console.error('[SubscriptionContext] Error updating profile for upgrade:', error);
          }
        } else {
          // If the entitlement is active, keep updating expiration date if it changed
          const isDateDifferent = profile.subscription_end_date !== expirationDate;
          if (hasActiveEntitlement && isDateDifferent) {
            console.log('[SubscriptionContext] Expiration date changed. Updating database...');
            await supabase
              .from('profiles')
              .update({ subscription_end_date: expirationDate })
              .eq('id', user.id);
            await refreshProfile();
          } else {
            console.log('[SubscriptionContext] RevenueCat and database are in sync');
          }
        }
      } catch (err) {
        console.error('[SubscriptionContext] Error syncing RevenueCat status:', err);
      }
    };

    syncRevenueCatStatus();
  }, [user?.id, profile?.id, hasSyncedRevenueCat, refreshProfile]);

  // Cache profile whenever it updates (for offline use)
  useEffect(() => {
    if (user && profile) {
      cacheSubscriptionProfile(user.id, profile);
      setIsOfflineMode(false); // We have fresh data
      setCachedSubscription(null);
    }
  }, [user, profile]);

  // Try to restore from cache if profile is null but user exists (offline scenario)
  useEffect(() => {
    if (user && !profile) {
      const cached = getCachedSubscription(user.id);
      if (cached) {
        console.log('[SubscriptionContext] Using cached subscription status (offline mode)');
        setCachedSubscription(cached);
        setIsOfflineMode(true);
      }
    }
  }, [user, profile]);

  // Load visitor quote status from localStorage on mount/user change
  useEffect(() => {
    if (!user) {
      const hasGuestQuote = !!localStorage.getItem(GUEST_QUOTE_KEY);
      const usedKey = localStorage.getItem(VISITOR_QUOTE_USED_KEY) === 'true';
      const used = hasGuestQuote && usedKey;
      setVisitorQuoteUsedState(used);
    } else {
      setVisitorQuoteUsedState(false);
    }
  }, [user]);

  // Determine isPremium: use profile if available, otherwise use cache
  // GOLDEN RULE: User remains PRO until server explicitly says otherwise
  const isPremium = profile
    ? isUserProFromProfile(profile)
    : (cachedSubscription?.isPremium ?? false);
  
  // Important: if we're offline (no profile), these must come from cache as well,
  // otherwise the UI looks like it “turned free”.
  const rawPlanType: PlanType = profile?.plan_type ?? cachedSubscription?.planType ?? 'free';
  const planType: PlanType = (rawPlanType === 'free' && isPremium) ? 'pro' : rawPlanType;
  const subscriptionStatus: SubscriptionStatus = profile?.subscription_status ?? cachedSubscription?.subscriptionStatus ?? null;
  const subscriptionEndDate: string | null = profile?.subscription_end_date ?? cachedSubscription?.subscriptionEndDate ?? null;
  
  const storedQuotesCreatedInPeriod =
    profile?.quotes_created_in_period ?? cachedSubscription?.quotesCreatedInPeriod ?? 0;
  const storedQuotesPeriodStart =
    profile?.quotes_period_start ?? cachedSubscription?.quotesPeriodStart ?? null;
  const storedBonusQuotes = profile?.bonus_quotes ?? cachedSubscription?.bonusQuotes ?? 0;
  const storedBonusPeriodStart =
    profile?.bonus_quotes_period_start ?? cachedSubscription?.bonusQuotesPeriodStart ?? null;

  // Lazy read reset: stale usage and stale referral allowance display as zero
  // immediately, including offline. No server write is needed until creation.
  const serverQuotesCreatedInPeriod = getCurrentPeriodValue(
    storedQuotesCreatedInPeriod,
    storedQuotesPeriodStart,
    quotaPeriodStart,
  );
  const serverBonusQuotes = getCurrentPeriodValue(
    storedBonusQuotes,
    storedBonusPeriodStart,
    quotaPeriodStart,
  );
  const localQuota = optimisticQuota?.userId === user?.id
    && optimisticQuota?.periodStart === quotaPeriodStart
    ? optimisticQuota
    : null;
  const quotesCreatedInPeriod = Math.max(
    serverQuotesCreatedInPeriod,
    localQuota?.quotesCreatedInPeriod ?? 0,
  );
  const bonusQuotes = localQuota?.bonusQuotes ?? serverBonusQuotes;
  const freeQuoteAllowance = getFreeQuoteAllowance(FREE_MONTHLY_LIMIT, bonusQuotes);

  // Trial state — only reads from profile (not cache) because the trial fields
  // aren't cached; offline users just don't get the banner, but the golden rule
  // still keeps them PRO via `isPremium` above.
  const trialEndsAt: string | null = profile?.trial_ends_at ?? null;
  const trialDaysRemaining: number | null = (() => {
    if (!trialEndsAt) return null;
    const ms = new Date(trialEndsAt).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();
  const isInTrial: boolean = !!(
    trialEndsAt &&
    trialDaysRemaining !== null &&
    trialDaysRemaining > 0 &&
    (subscriptionStatus === 'trialing' || isPremium)
  );

  // Push OneSignal tag whenever the derived subscription "state" changes.
  useEffect(() => {
    if (!user) return;
    let state: OneSignalSubscriptionTag;
    if (isInTrial) state = 'trial';
    else if (isPremium) state = 'pro';
    else if (subscriptionStatus === 'canceled') state = 'churned';
    else state = 'free';
    setOneSignalSubscriptionTag(state);
  }, [user?.id, isPremium, isInTrial, subscriptionStatus]);

  // Function to set visitor quote as used
  const setVisitorQuoteUsed = (used: boolean) => {
    if (used) {
      localStorage.setItem(VISITOR_QUOTE_USED_KEY, 'true');
    } else {
      localStorage.removeItem(VISITOR_QUOTE_USED_KEY);
      localStorage.removeItem(GUEST_QUOTE_KEY);
    }
    setVisitorQuoteUsedState(used);
  };

  useEffect(() => {
    if (!optimisticQuota || !user || optimisticQuota.userId !== user.id) return;
    if (storedQuotesPeriodStart !== optimisticQuota.periodStart) return;
    if (storedQuotesCreatedInPeriod < optimisticQuota.quotesCreatedInPeriod) return;
    setOptimisticQuota(null);
  }, [
    optimisticQuota,
    storedQuotesCreatedInPeriod,
    storedQuotesPeriodStart,
    user,
  ]);

  useEffect(() => {
    if (!user) return;
    const refreshQuota = () => {
      void refreshProfile();
    };
    window.addEventListener('quote-quota-updated', refreshQuota);
    return () => window.removeEventListener('quote-quota-updated', refreshQuota);
  }, [refreshProfile, user]);

  const cacheOptimisticProfile = (
    nextQuotesCreatedInPeriod: number,
    nextBonusQuotes: number,
    countTowardQuota: boolean,
  ) => {
    if (!user || !profile) return;

    const nextProfile = {
      ...profile,
      lifetime_quotes_created: (profile.lifetime_quotes_created ?? 0) + 1,
      quotes_created_in_period: countTowardQuota
        ? nextQuotesCreatedInPeriod
        : profile.quotes_created_in_period,
      quotes_period_start: countTowardQuota
        ? quotaPeriodStart
        : profile.quotes_period_start,
      // A stale bonus is zero for the local month. Keep both fields aligned in
      // the offline cache so an app restart cannot resurrect expired credit.
      bonus_quotes: countTowardQuota ? nextBonusQuotes : profile.bonus_quotes,
      bonus_quotes_period_start: countTowardQuota
        ? quotaPeriodStart
        : profile.bonus_quotes_period_start,
    };

    cacheSubscriptionProfile(user.id, nextProfile);
    cacheOfflineDataProfile(user.id, nextProfile);
  };

  // Records a successful quote creation. The RPC keeps the lifetime sequence
  // monotonic and updates/reset the monthly counter in the same row UPDATE.
  const incrementQuoteCount = async ({
    offline = false,
    countTowardQuota = true,
    sequenceId = null,
  }: {
    offline?: boolean;
    countTowardQuota?: boolean;
    sequenceId?: number | null;
  } = {}) => {
    if (!user) return;

    const shouldCountLocally = countTowardQuota && !isPremium;
    const nextQuotesCreatedInPeriod = shouldCountLocally
      ? quotesCreatedInPeriod + 1
      : quotesCreatedInPeriod;
    const nextBonusQuotes = shouldCountLocally ? bonusQuotes : serverBonusQuotes;

    if (offline) {
      if (shouldCountLocally) {
        setOptimisticQuota({
          userId: user.id,
          periodStart: quotaPeriodStart,
          quotesCreatedInPeriod: nextQuotesCreatedInPeriod,
          bonusQuotes: nextBonusQuotes,
        });
      }
      cacheOptimisticProfile(
        nextQuotesCreatedInPeriod,
        nextBonusQuotes,
        shouldCountLocally,
      );
      return;
    }

    const { data, error } = await supabase.rpc('record_quote_creation', {
      p_period_start: quotaPeriodStart,
      p_count_toward_quota: countTowardQuota,
      ...(sequenceId == null ? {} : { p_sequence_id: sequenceId }),
    });

    if (error) {
      console.error('[SubscriptionContext] Failed to record quote creation:', error);
      return;
    }

    const updated = data?.[0];
    if (updated) {
      const updatedQuotes = getCurrentPeriodValue(
        updated.quotes_created_in_period,
        updated.quotes_period_start,
        quotaPeriodStart,
      );
      const updatedBonus = getCurrentPeriodValue(
        updated.bonus_quotes,
        updated.bonus_quotes_period_start,
        quotaPeriodStart,
      );
      if (countTowardQuota && !isPremium) {
        setOptimisticQuota({
          userId: user.id,
          periodStart: quotaPeriodStart,
          quotesCreatedInPeriod: updatedQuotes,
          bonusQuotes: updatedBonus,
        });
      }
    }

    await refreshProfile();
  };

  useEffect(() => {
    if (!user) return;
    const recordOfflineImport = () => {
      void incrementQuoteCount({ offline: true, countTowardQuota: true });
    };
    window.addEventListener('quote-created-offline', recordOfflineImport);
    return () => window.removeEventListener('quote-created-offline', recordOfflineImport);
  }, [
    bonusQuotes,
    isPremium,
    profile,
    quotaPeriodStart,
    quotesCreatedInPeriod,
    user,
  ]);

  // Calculate remaining quotes based on user type
  const calculateCanCreateQuote = (): boolean => {
    if (isPremium) return true;
    
    if (user) {
      return quotesCreatedInPeriod < freeQuoteAllowance;
    } else {
      // Visitor: check if they've already used their one quote
      return !visitorQuoteUsed;
    }
  };

  const calculateRemainingQuotes = (): number => {
    if (isPremium) return Infinity;
    
    if (user) {
      return Math.max(0, freeQuoteAllowance - quotesCreatedInPeriod);
    } else {
      // Visitor: 1 if not used, 0 if used
      return visitorQuoteUsed ? 0 : 1;
    }
  };

  const canCreateQuote = calculateCanCreateQuote();
  const remainingQuotes = calculateRemainingQuotes();

  return (
    <SubscriptionContext.Provider
      value={{
        isPremium,
        planType,
        subscriptionStatus,
        subscriptionEndDate,
        quotesCreatedInPeriod,
        quotaPeriodStart,
        canCreateQuote,
        remainingQuotes,
        freeQuotesLimit: freeQuoteAllowance,
        incrementQuoteCount,
        isInTrial,
        trialDaysRemaining,
        trialEndsAt,
        visitorQuoteUsed,
        setVisitorQuoteUsed,
        isOfflineMode,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
